import {Checkout, Customer, IGetCheckoutShippingOptionsData} from "@connox/typescript-library";
import * as Sentry from "@sentry/browser";
import {Severity} from "@sentry/browser";
import * as React from "react";
import {useRef, useState} from "react";
import {IViewState, useCartContext} from "../../cart/CartContextProvider/CartContextProvider";
import {useCustomerContext} from "../../customer/CustomerContextProvider/CustomerContextProvider";
import {PaymentController, Payments} from "../../lib/payments/PaymentController";
import window from "../../lib/WindowExtended";
import {CheckoutStep} from "../Checkout";
import {useCheckoutContext} from "./CheckoutContextProvider";
import {isAmazonLoggedIn} from "../../lib/payments/AmazonPayLibrary";

interface ICheckoutViewContext {
  step: CheckoutStep;
  gotoStep: (step?: CheckoutStep, viewState?: IViewState) => void;
  shippingOptions?: IGetCheckoutShippingOptionsData[];
}

const checkoutViewContext = React.createContext<ICheckoutViewContext | null>(null);

export const useCheckoutViewContext = (): ICheckoutViewContext => {
  const context = React.useContext(checkoutViewContext);
  if (!context) {
    throw new Error("useCheckoutViewContext must be used in a component within a CheckoutViewContextProvider.");
  }

  return context;
};

export const isSuccessfulAmazonCheckout = (): boolean => {
  const uParams = new URLSearchParams(window.location.search);

  return uParams.get("gotoStep") === CheckoutStep.SUMMARY && uParams.get("AuthenticationStatus") === "Success";
};

interface IAmazonState {
  isAddressNeeded: boolean;
  isPaymentNeeded: boolean;
}

export const amazonState = (paymentId: number | null): IAmazonState => {
  const tempState = {
    isAddressNeeded: false,
    isPaymentNeeded: false,
  };

  if (paymentId === Payments.AmazonPay && window.orderReferenceId) {
    tempState.isAddressNeeded = !window.isBillingAgreementAddressSet;
    tempState.isPaymentNeeded = !window.isBillingAgreementPaymentSet;
  }

  return tempState;
};

const isExternalPayment = (): boolean => !!(window.pp_nonce || window.orderReferenceId);

const isValidForSkippingOverAddressStep = (checkout: Checkout): boolean => {
  // if addresses valid, return true to skip address step
  if (isAddressesValid(checkout)) {
    return true;
  }

  // if addresses not valid

  if (
    /* if message of invalid phone number present */
    !!checkout.deliveryAddress?.messages?.find((msg) => msg.field === "phone") &&
    /* and above message is the only one */
    checkout.deliveryAddress?.messages?.length === 1
  ) {
    // if corporate customer, it happens in the address step (return false)
    // if not, it's an invalid forwarder number (will be caught in shipping step)
    return !checkout.cartData.isCorporateCustomer;
  } else {
    // if no message of invalid phone number present, or more than this message

    // if amazon pay user, return true (will be skipped by amazonState(paymentId).isAddressNeeded)
    // if not apay user, return false. seems to be errors in addresses
    return isAmazonLoggedIn();
  }
};

const toStopAtShippingStep = (checkout: Checkout, shippingOptions: IGetCheckoutShippingOptionsData[] | undefined) => {
  return (
    !checkout.logisticsTypeId ||
    !shippingOptions?.find((ship) => ship.logisticsTypeId === checkout.logisticsTypeId) ||
    checkout.cartData.messages.find((msg) => msg.code === "NO_SHIPPING_SELECTED") ||
    (shippingOptions?.find((ship) => ship.logisticsTypeId === checkout.logisticsTypeId)?.isPhoneRequired &&
      (!checkout.deliveryAddress?.phone || !!checkout.deliveryAddress?.messages?.find((msg) => msg.field === "phone")))
  );
};

/**
 * return appropriate step if none is given
 * @param checkout
 * @param customer
 * @param viewState
 * @param shippingOptions
 */
const returnAppropriateStep = (
  checkout: Checkout,
  customer: Customer,
  viewState: IViewState,
  shippingOptions?: IGetCheckoutShippingOptionsData[],
): CheckoutStep => {
  const isItemPositionsInCart = !!checkout.cartData.positions.find(
    (position) => position.reference && position.reference.itemId !== null && !!position.item,
  );

  let returnStep: CheckoutStep;
  if (isSuccessfulAmazonCheckout() && isItemPositionsInCart) {
    return checkout.isCompleted ? CheckoutStep.FINISHED : CheckoutStep.SUMMARY;
  }

  if (!isItemPositionsInCart) {
    returnStep = CheckoutStep.CARTERRORS;
  } else if (!(customer.isLoggedIn || viewState.isRegistering || isExternalPayment())) {
    returnStep = CheckoutStep.LOGIN;
  } else if (
    !isValidForSkippingOverAddressStep(checkout) ||
    amazonState(checkout.paymentId).isAddressNeeded ||
    !!checkout.cartData.positions.find((pos) => {
      return !!pos.messages.find((msg) => msg.code === "NOT_ALLOWED_FOR_COUNTRY" || msg.code === "SHIPPING_NOT_FOUND");
    })
  ) {
    returnStep = CheckoutStep.ADDRESS;
  } else if (toStopAtShippingStep(checkout, shippingOptions)) {
    returnStep = CheckoutStep.SHIPPING;
    // } else if (!(checkout.paymentId && (checkout.paymentId !== Payments.AmazonPay && !window.isBillingAgreementPaymentSet) && PaymentController.isDataComplete())) {
  } else if (
    !checkout.paymentId ||
    amazonState(checkout.paymentId).isPaymentNeeded ||
    !PaymentController.isDataComplete()
  ) {
    returnStep = CheckoutStep.PAYMENT;
  } else if (!checkout.isCompleted) {
    returnStep = CheckoutStep.SUMMARY;
  } else {
    returnStep = CheckoutStep.FINISHED;
  }

  return returnStep;
};

/**
 * Context provider for view params
 * @param props
 * @constructor
 */
const CheckoutViewContextProvider: React.FC<{children?: React.ReactNode}> = (props) => {
  const {customer} = useCustomerContext();
  const {setViewState, viewState} = useCartContext();
  const {checkout} = useCheckoutContext();
  const viewStateRef = useRef<IViewState>(viewState);
  const [{deliveryCountry, shippingOptions, step}, setShippingData] = useState<{
    deliveryCountry?: string;
    shippingOptions?: IGetCheckoutShippingOptionsData[];
    step: CheckoutStep;
  }>(() => {
    const firstStep = returnAppropriateStep(checkout, customer, viewState, []);
    sentryAddBreadcrumb(
      firstStep,
      checkout.logisticsTypeId,
      checkout.paymentId,
      "checkout view context provider init",
      undefined,
      customer,
    );

    return {
      shippingOptions: [],
      step: firstStep,
    };
  });
  viewStateRef.current = viewState;

  const gotoStep = (nextStep?: CheckoutStep, stateOfView?: IViewState) => {
    if (stateOfView !== undefined) {
      viewStateRef.current = stateOfView;
      setViewState(stateOfView);
    }
    const tempNextStep =
      nextStep || returnAppropriateStep(checkout, customer, stateOfView || viewState, shippingOptions);
    const reason = nextStep ? "Breadcrumb or Summary" : "continue from amazon pay wallet widget";
    sentryAddBreadcrumb(
      tempNextStep,
      checkout.logisticsTypeId,
      checkout.paymentId,
      reason,
      shippingOptions,
      customer,
    );
    setShippingData({
      deliveryCountry,
      shippingOptions,
      step: tempNextStep,
    });
  };

  const setAllThings = async () => {
    const deliveryAddressCountry = checkout.deliveryAddress?.countryCode;
    if (deliveryAddressCountry !== deliveryCountry) {
      const {deliveryAddress} = checkout;
      let shipOptions = shippingOptions;
      // when invoice address isn't filled at all (kind of pre-fetch, when address step is
      // initially shown), or countryCode is (re-)set (response will most likely throw 400 if not)
      if (deliveryAddress === null || deliveryAddress.countryCode !== "") {
        try {
          shipOptions = await checkout.getAvailableShippingOptions();
          const appropiateStep = returnAppropriateStep(checkout, customer, viewStateRef.current, shipOptions);

          sentryAddBreadcrumb(
            appropiateStep,
            checkout.logisticsTypeId,
            checkout.paymentId,
            "Country Code changed",
            shipOptions,
            customer,
          );
          setShippingData({
            deliveryCountry: deliveryAddressCountry,
            shippingOptions: shipOptions,
            step: appropiateStep,
          });
        } catch (err) {
          Sentry.captureException(err);
          setShippingData({deliveryCountry, shippingOptions: undefined, step});
        }
      }
    } else {
      const nextStepToGo = returnAppropriateStep(checkout, customer, viewStateRef.current, shippingOptions);
      sentryAddBreadcrumb(
        nextStepToGo,
        checkout.logisticsTypeId,
        checkout.paymentId,
        "checkout or customer emit",
        shippingOptions,
        customer,
      );
      setShippingData({
        deliveryCountry,
        shippingOptions,
        step: nextStepToGo,
      });
    }
  };

  React.useEffect(() => {
    if (checkout.paymentId) {
      const uspAuthenticationStatus = new URLSearchParams(window.location.search).get("AuthenticationStatus");
      if (checkout.paymentId === Payments.AmazonPay && !window.orderReferenceId && !uspAuthenticationStatus) {
        void checkout.put({paymentId: 0});
      } else {
        PaymentController.createPayment(checkout, checkout.paymentId);
      }
    }

    void setAllThings();
    checkout.emitter.on(setAllThings);
    customer.emitter.on(setAllThings);

    return () => {
      checkout.emitter.off(setAllThings);
      customer.emitter.off(setAllThings);
    };
  }, []);

  if (!checkoutViewContext) {
    return null;
  }

  const {Provider} = checkoutViewContext;

  return <Provider value={{step, gotoStep, shippingOptions}}>{props.children}</Provider>;
};

/**
 * Check addresses for errors
 * @param checkout
 */
const isAddressesValid = (checkout: Checkout): boolean => {
  const {deliveryAddress, invoiceAddress} = checkout;

  return !(
    deliveryAddress === null ||
    invoiceAddress === null ||
    deliveryAddress.messages?.find((message) => message.type === "error") ||
    invoiceAddress.messages?.find((message) => message.type === "error")
  );
};

const sentryAddBreadcrumb = (
  step: CheckoutStep,
  logisticsType: number | null,
  paymentType: number | null,
  reason: string,
  shippings: IGetCheckoutShippingOptionsData[] | undefined,
  customer: Customer,
) => {
  Sentry.addBreadcrumb({
    category: "step.change",
    level: Severity.Info,
    message: step,
    type: "User Action",
    data: {
      customerStatus: customer.isLoggedIn ? "logged in" : customer.isRecognised ? "recognized" : "new",
      isExternalPayment: isExternalPayment(),
      amazonInfo: isExternalPayment()
        ? {
          loggedIn: isAmazonLoggedIn(),
          urlParamsSet: isSuccessfulAmazonCheckout(),
          amazonState: amazonState(paymentType),
          referenceIdSet: !!window.orderReferenceId,
        }
        : "none",
      checkoutData: {
        logisticsType,
        paymentType,
      },
      reason,
      shippings: shippings ? shippings.map((so) => so.logisticsTypeId) : [],
    },
  });
};

export default CheckoutViewContextProvider;
